/**
 * CCD Home Intro - Scroll Animation Web Component
 *
 * Animates two product images from a stacked position in the hero section
 * to their final positions in product cards as the user scrolls.
 */

class HomeIntroAnimation extends HTMLElement {
  constructor() {
    super();
    this.imagesDocked = false;
    this._scrollRafId = null;
    this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  connectedCallback() {
    // Check for reduced motion preference
    if (this.prefersReducedMotion) {
      this.handleReducedMotion();
      return;
    }

    // Get configuration from data attributes
    this.sectionId = this.dataset.sectionId;
    this.initialRotation = parseFloat(this.dataset.initialRotation) || -10;

    // Cache DOM elements
    this.floatingContainer = document.getElementById(`floatingImages-${this.sectionId}`);
    this.leftImg = document.getElementById(`floatingImg-left-${this.sectionId}`);
    this.rightImg = document.getElementById(`floatingImg-right-${this.sectionId}`);
    this.leftCard = document.getElementById(`productCard-left-${this.sectionId}`);
    this.rightCard = document.getElementById(`productCard-right-${this.sectionId}`);
    this.leftInfo = document.getElementById(`productInfo-left-${this.sectionId}`);
    this.rightInfo = document.getElementById(`productInfo-right-${this.sectionId}`);
    this.heroSection = document.getElementById(`heroSection-${this.sectionId}`);
    this.heroTitle = document.getElementById(`heroTitle-${this.sectionId}`);
    this.heroDrops = document.getElementById(`heroDrops-${this.sectionId}`);
    this.heroGlow = document.getElementById(`heroGlow-${this.sectionId}`);
    this.heroShopNow = document.getElementById(`heroShopNow-${this.sectionId}`);

    // Verify all elements exist
    if (
      !this.floatingContainer ||
      !this.leftImg ||
      !this.rightImg ||
      !this.leftCard ||
      !this.rightCard ||
      !this.leftInfo ||
      !this.rightInfo
    ) {
      console.warn("HomeIntroAnimation: Missing required DOM elements");
      return;
    }

    // Bind event handlers
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.debounce(this.handleResize.bind(this), 100);

    // Add event listeners
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("resize", this.handleResize, { passive: true });

    // Fit watermark text to container once fonts are loaded
    document.fonts.ready.then(() => this.fitWatermarkText());

    // Initial position calculation
    this.handleScroll();
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleResize);
    if (this._scrollRafId) {
      cancelAnimationFrame(this._scrollRafId);
    }
  }

  /**
   * Handle reduced motion preference - show static layout
   */
  handleReducedMotion() {
    this.classList.add("reduced-motion", "images-docked");

    // Fit watermark text once fonts are loaded
    document.fonts.ready.then(() => this.fitWatermarkText());

    // Move images directly to placeholders
    const leftPlaceholder = this.querySelector(
      `#productCard-left-${this.dataset.sectionId} .home-intro__image-placeholder`,
    );
    const rightPlaceholder = this.querySelector(
      `#productCard-right-${this.dataset.sectionId} .home-intro__image-placeholder`,
    );
    const leftImg = document.getElementById(`floatingImg-left-${this.dataset.sectionId}`);
    const rightImg = document.getElementById(`floatingImg-right-${this.dataset.sectionId}`);
    const leftInfo = document.getElementById(`productInfo-left-${this.dataset.sectionId}`);
    const rightInfo = document.getElementById(`productInfo-right-${this.dataset.sectionId}`);

    if (leftPlaceholder && leftImg) {
      leftImg.style.transform = "none";
      leftImg.style.position = "relative";
      leftPlaceholder.appendChild(leftImg);
    }

    if (rightPlaceholder && rightImg) {
      rightImg.style.transform = "none";
      rightImg.style.position = "relative";
      rightPlaceholder.appendChild(rightImg);
    }

    // Add is-docked class to product cards (enables hover animation)
    const leftCard = document.getElementById(`productCard-left-${this.dataset.sectionId}`);
    const rightCard = document.getElementById(`productCard-right-${this.dataset.sectionId}`);
    if (leftCard) leftCard.classList.add("is-docked");
    if (rightCard) rightCard.classList.add("is-docked");

    // Show product info immediately
    if (leftInfo) leftInfo.classList.add("visible");
    if (rightInfo) rightInfo.classList.add("visible");

    // Hide floating container and hero
    const floatingContainer = document.getElementById(`floatingImages-${this.dataset.sectionId}`);
    const heroSection = document.getElementById(`heroSection-${this.dataset.sectionId}`);
    if (floatingContainer) floatingContainer.style.display = "none";
    if (heroSection) heroSection.style.display = "none";
  }

  /**
   * Check if viewport is mobile size
   */
  isMobile() {
    return window.innerWidth <= 749;
  }

  /**
   * Adjust watermark SVG viewBoxes to tightly wrap the rendered text,
   * so the text scales to fill the container width.
   */
  fitWatermarkText() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    this.querySelectorAll('.home-intro__watermark-text svg').forEach(svg => {
      const textEl = svg.querySelector('text');
      if (!textEl) return;
      const bbox = textEl.getBBox();
      if (bbox.width <= 0 || bbox.height <= 0) return;

      // Use canvas measureText to get actual ink bounds vs full em-box
      const style = getComputedStyle(textEl);
      const testSize = 100;
      ctx.font = `${style.fontWeight} ${testSize}px ${style.fontFamily}`;
      const metrics = ctx.measureText(textEl.textContent);

      const emHeight = metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
      const inkHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

      if (emHeight <= 0 || inkHeight <= 0) {
        svg.setAttribute('viewBox', `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
        return;
      }

      // Map ink bounds onto the SVG bbox
      const topPadding = (metrics.fontBoundingBoxAscent - metrics.actualBoundingBoxAscent) / emHeight * bbox.height;
      const tightHeight = (inkHeight / emHeight) * bbox.height;

      svg.setAttribute('viewBox', `${bbox.x} ${bbox.y + topPadding} ${bbox.width} ${tightHeight}`);
    });
  }

  /**
   * Scroll handler - batches updates via requestAnimationFrame
   */
  handleScroll() {
    if (this._scrollRafId) return;
    this._scrollRafId = requestAnimationFrame(() => {
      this._scrollRafId = null;
      this._updateScrollAnimation();
    });
  }

  /**
   * Main scroll animation update - calculates positions and updates transforms
   */
  _updateScrollAnimation() {
    const scrollTop = window.scrollY;
    const windowHeight = window.innerHeight;

    // Calculate scroll progress - animation plays over one hero height of scrolling
    const heroHeight = this.heroSection ? this.heroSection.offsetHeight : windowHeight;
    const scrollProgress = Math.min(Math.max(scrollTop / heroHeight, 0), 1);

    // Hysteresis: dock at 100%, undock at 95%
    if (scrollProgress >= 1 && !this.imagesDocked) {
      this.dockImages();
      return;
    } else if (scrollProgress < 0.95 && this.imagesDocked) {
      this.undockImages(scrollProgress);
    }

    // Skip animation calculations when docked
    if (this.imagesDocked) return;

    // Get target positions (center of each product card's image placeholder)
    const leftPlaceholder = this.leftCard.querySelector(".home-intro__image-placeholder");
    const rightPlaceholder = this.rightCard.querySelector(".home-intro__image-placeholder");

    const leftRect = leftPlaceholder.getBoundingClientRect();
    const rightRect = rightPlaceholder.getBoundingClientRect();

    // Starting position (center of viewport)
    const startX = window.innerWidth / 2;
    const startY = windowHeight / 2;

    // Target positions: where the cards WILL BE when animation completes (scroll = heroHeight)
    const leftTargetX = leftRect.left + leftRect.width / 2;
    const leftTargetY = leftRect.top + scrollTop - heroHeight + leftRect.height / 2;
    const rightTargetX = rightRect.left + rightRect.width / 2;
    const rightTargetY = rightRect.top + scrollTop - heroHeight + rightRect.height / 2;

    // Calculate rotations
    const leftRotation = this.initialRotation * (1 - scrollProgress);
    const rightRotation = this.initialRotation * (1 - scrollProgress);

    // Interpolate positions
    let leftX, leftY, rightX, rightY;

    if (this.isMobile()) {
      // Mobile: Images stay centered horizontally, only move vertically
      const centerX = window.innerWidth / 2;
      leftX = centerX;
      rightX = centerX;

      leftY = startY + (leftTargetY - startY) * scrollProgress;
      rightY = startY + (rightTargetY - startY) * scrollProgress;
    } else {
      // Desktop: X and Y movement
      leftX = startX + (leftTargetX - startX) * scrollProgress;
      leftY = startY + (leftTargetY - startY) * scrollProgress;
      rightX = startX + (rightTargetX - startX) * scrollProgress;
      rightY = startY + (rightTargetY - startY) * scrollProgress;
    }

    // Apply transforms
    if (this.isMobile()) {
      const offsetX = leftX - startX;
      const offsetY = leftY - startY;
      this.leftImg.style.transform = `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px)) rotate(${leftRotation}deg)`;

      const rightOffsetX = rightX - startX;
      const rightOffsetY = rightY - startY;
      this.rightImg.style.transform = `translate(calc(-50% + ${rightOffsetX}px), calc(-50% + ${rightOffsetY}px)) rotate(${rightRotation}deg)`;
    } else {
      this.leftImg.style.transform = `translate(${leftX - startX}px, ${leftY - startY}px) rotate(${leftRotation}deg)`;
      this.rightImg.style.transform = `translate(${rightX - startX}px, ${rightY - startY}px) rotate(${rightRotation}deg)`;
    }

    // Fade out hero elements using raw (linear) progress for consistent timing
    const fadeOpacity = 1 - scrollProgress;
    if (this.heroTitle) {
      this.heroTitle.style.opacity = fadeOpacity;
    }

    // Fade out glow quickly (fully gone by 30% scroll progress)
    if (this.heroGlow) {
      const glowOpacity = Math.max(0, 0.5 * (1 - scrollProgress / 0.3));
      this.heroGlow.style.opacity = glowOpacity;
    }

    // Show product info when animation is mostly complete (70%+)
    if (scrollProgress > 0.7) {
      this.leftInfo.classList.add("visible");
      this.rightInfo.classList.add("visible");
    } else {
      this.leftInfo.classList.remove("visible");
      this.rightInfo.classList.remove("visible");
    }
  }

  /**
   * Dock images into their final positions using FLIP animation
   */
  dockImages() {
    if (this.imagesDocked) return;

    const leftPlaceholder = this.leftCard.querySelector(".home-intro__image-placeholder");
    const rightPlaceholder = this.rightCard.querySelector(".home-intro__image-placeholder");

    // FLIP Step 1: Record current screen positions (First)
    const leftFirst = this.leftImg.getBoundingClientRect();
    const rightFirst = this.rightImg.getBoundingClientRect();

    // Reset transforms and positioning
    this.leftImg.style.transform = "none";
    this.leftImg.style.position = "relative";
    this.rightImg.style.transform = "none";
    this.rightImg.style.position = "relative";

    // On mobile, also reset left/top since we use transform-based centering
    if (this.isMobile()) {
      this.leftImg.style.left = "auto";
      this.leftImg.style.top = "auto";
      this.rightImg.style.left = "auto";
      this.rightImg.style.top = "auto";
    }

    // Move images to placeholders
    leftPlaceholder.appendChild(this.leftImg);
    rightPlaceholder.appendChild(this.rightImg);

    // Hide the floating container
    this.floatingContainer.style.display = "none";

    // Hide hero title and glow
    if (this.heroTitle) {
      this.heroTitle.style.opacity = "0";
    }
    if (this.heroGlow) {
      this.heroGlow.style.opacity = "0";
    }

    // Add docked state class
    this.classList.add("images-docked");

    // Add is-docked class to product cards (enables hover animation)
    this.leftCard.classList.add("is-docked");
    this.rightCard.classList.add("is-docked");

    // Ensure product info is visible
    this.leftInfo.classList.add("visible");
    this.rightInfo.classList.add("visible");

    this.imagesDocked = true;

    // FLIP Step 2: Record new screen positions (Last)
    const leftLast = this.leftImg.getBoundingClientRect();
    const rightLast = this.rightImg.getBoundingClientRect();

    // FLIP Step 3: Calculate deltas (Invert)
    const leftDx = leftFirst.left - leftLast.left;
    const leftDy = leftFirst.top - leftLast.top;
    const leftSx = leftFirst.width / (leftLast.width || 1);
    const leftSy = leftFirst.height / (leftLast.height || 1);

    const rightDx = rightFirst.left - rightLast.left;
    const rightDy = rightFirst.top - rightLast.top;
    const rightSx = rightFirst.width / (rightLast.width || 1);
    const rightSy = rightFirst.height / (rightLast.height || 1);

    // If the delta is tiny, skip the FLIP animation (already in place)
    const threshold = 2;
    const needsFlip =
      Math.abs(leftDx) > threshold ||
      Math.abs(leftDy) > threshold ||
      Math.abs(rightDx) > threshold ||
      Math.abs(rightDy) > threshold;

    if (!needsFlip) return;

    // Apply inverse transform so visually nothing changes yet
    this.leftImg.style.transformOrigin = "top left";
    this.leftImg.style.transform = `translate(${leftDx}px, ${leftDy}px) scale(${leftSx}, ${leftSy})`;
    this.rightImg.style.transformOrigin = "top left";
    this.rightImg.style.transform = `translate(${rightDx}px, ${rightDy}px) scale(${rightSx}, ${rightSy})`;

    // FLIP Step 4: Animate to final position (Play)
    requestAnimationFrame(() => {
      this.leftImg.style.transition = "transform 0.3s ease-out";
      this.leftImg.style.transform = "none";
      this.rightImg.style.transition = "transform 0.3s ease-out";
      this.rightImg.style.transform = "none";

      // Clean up after animation completes
      const cleanup = () => {
        this.leftImg.style.transition = "";
        this.leftImg.style.transformOrigin = "";
        this.rightImg.style.transition = "";
        this.rightImg.style.transformOrigin = "";
      };
      this.leftImg.addEventListener("transitionend", cleanup, { once: true });
      setTimeout(cleanup, 350); // Fallback
    });
  }

  /**
   * Undock images - move them back to floating container and resume animation
   */
  undockImages(scrollProgress) {
    if (!this.imagesDocked) return;

    // Clear any running FLIP transition
    this.leftImg.style.transition = "";
    this.leftImg.style.transformOrigin = "";
    this.rightImg.style.transition = "";
    this.rightImg.style.transformOrigin = "";

    const windowHeight = window.innerHeight;
    const heroHeight = this.heroSection ? this.heroSection.offsetHeight : windowHeight;

    // Get placeholder positions for calculating where images currently are
    const leftPlaceholder = this.leftCard.querySelector(".home-intro__image-placeholder");
    const rightPlaceholder = this.rightCard.querySelector(".home-intro__image-placeholder");
    const leftRect = leftPlaceholder.getBoundingClientRect();
    const rightRect = rightPlaceholder.getBoundingClientRect();

    // Starting position (center of viewport)
    const startX = window.innerWidth / 2;
    const startY = windowHeight / 2;

    // Calculate current positions based on scroll progress
    const scrollTop = window.scrollY;
    const leftTargetX = leftRect.left + leftRect.width / 2;
    const leftTargetY = leftRect.top + scrollTop - heroHeight + leftRect.height / 2;
    const rightTargetX = rightRect.left + rightRect.width / 2;
    const rightTargetY = rightRect.top + scrollTop - heroHeight + rightRect.height / 2;

    const leftRotation = this.initialRotation * (1 - scrollProgress);
    const rightRotation = this.initialRotation * (1 - scrollProgress);

    let leftX, leftY, rightX, rightY;

    if (this.isMobile()) {
      // Mobile: Images stay centered horizontally, only move vertically
      const centerX = window.innerWidth / 2;
      leftX = centerX;
      rightX = centerX;

      leftY = startY + (leftTargetY - startY) * scrollProgress;
      rightY = startY + (rightTargetY - startY) * scrollProgress;
    } else {
      leftX = startX + (leftTargetX - startX) * scrollProgress;
      leftY = startY + (leftTargetY - startY) * scrollProgress;
      rightX = startX + (rightTargetX - startX) * scrollProgress;
      rightY = startY + (rightTargetY - startY) * scrollProgress;
    }

    // Show floating container first
    this.floatingContainer.style.display = "";

    // Move images back to floating container with correct transforms
    this.floatingContainer.appendChild(this.leftImg);
    this.floatingContainer.appendChild(this.rightImg);

    // Reset positioning and apply transforms
    this.leftImg.style.position = "absolute";
    this.rightImg.style.position = "absolute";

    // On mobile, restore left/top for transform-based centering
    if (this.isMobile()) {
      this.leftImg.style.left = "50%";
      this.leftImg.style.top = "50%";
      this.rightImg.style.left = "50%";
      this.rightImg.style.top = "50%";
    }

    // Apply transforms
    if (this.isMobile()) {
      const offsetX = leftX - startX;
      const offsetY = leftY - startY;
      this.leftImg.style.transform = `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px)) rotate(${leftRotation}deg)`;

      const rightOffsetX = rightX - startX;
      const rightOffsetY = rightY - startY;
      this.rightImg.style.transform = `translate(calc(-50% + ${rightOffsetX}px), calc(-50% + ${rightOffsetY}px)) rotate(${rightRotation}deg)`;
    } else {
      this.leftImg.style.transform = `translate(${leftX - startX}px, ${leftY - startY}px) rotate(${leftRotation}deg)`;
      this.rightImg.style.transform = `translate(${rightX - startX}px, ${rightY - startY}px) rotate(${rightRotation}deg)`;
    }

    // Restore hero elements based on scroll progress
    const fadeOpacity = 1 - scrollProgress;

    if (this.heroTitle) {
      this.heroTitle.style.opacity = fadeOpacity;
    }
    if (this.heroGlow) {
      const glowOpacity = Math.max(0, 0.5 * (1 - scrollProgress / 0.3));
      this.heroGlow.style.opacity = glowOpacity;
    }

    // Update product info visibility based on scroll progress
    if (scrollProgress > 0.7) {
      this.leftInfo.classList.add("visible");
      this.rightInfo.classList.add("visible");
    } else {
      this.leftInfo.classList.remove("visible");
      this.rightInfo.classList.remove("visible");
    }

    // Remove docked state
    this.classList.remove("images-docked");

    // Remove is-docked class from product cards (disables hover animation)
    this.leftCard.classList.remove("is-docked");
    this.rightCard.classList.remove("is-docked");

    this.imagesDocked = false;
  }

  /**
   * Handle window resize - recalculate positions
   */
  handleResize() {
    this.handleScroll();
  }

  /**
   * Debounce utility function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Register the custom element
if (!customElements.get("home-intro-animation")) {
  customElements.define("home-intro-animation", HomeIntroAnimation);
}
